import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import tarotCards from "./tarotCards";
import DailyReading from "./components/DailyReading";
import { useZodiacStore } from "./store/useZodiacStore"; // Import the Zustand store

// Helper function to get date in YYYY-MM-DD format
const getTodaysDateString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Helper function to get a random card
function getRandomCard(deck) {
  const randomIndex = Math.floor(Math.random() * deck.length);
  return deck[randomIndex];
}

const ZODIAC_SIGNS = [
  "Aries",
  "Taurus",
  "Gemini",
  "Cancer",
  "Leo",
  "Virgo",
  "Libra",
  "Scorpio",
  "Sagittarius",
  "Capricorn",
  "Aquarius",
  "Pisces",
];

export default function HomePage() {
  const [dailyCard, setDailyCard] = useState(null);
  // Use Zustand store for selectedZodiac and selectedRisingSign
  const selectedZodiac = useZodiacStore((state) => state.selectedZodiac);
  const setSelectedZodiac = useZodiacStore((state) => state.setSelectedZodiac);
  const selectedRisingSign = useZodiacStore(
    (state) => state.selectedRisingSign
  );
  const setSelectedRisingSign = useZodiacStore(
    (state) => state.setSelectedRisingSign
  );

  // Reset signs on component mount
  useEffect(() => {
    setSelectedZodiac(""); // Reset sun sign
    setSelectedRisingSign(""); // Reset rising sign
  }, [setSelectedZodiac, setSelectedRisingSign]); // Include setters in dependency array

  // Draw daily card on mount, persisting for the day
  useEffect(() => {
    const storageKey = "dailyTarotData";
    const todaysDate = getTodaysDateString();
    let storedCard = null;

    try {
      const storedDataString = localStorage.getItem(storageKey);
      if (storedDataString) {
        const storedData = JSON.parse(storedDataString);
        // Check if the stored data is for today
        if (storedData.dateDrawn === todaysDate && storedData.card) {
          storedCard = storedData.card;
          // console.log("Using stored daily card for today:", storedCard);
        } else {
          // console.log("Stored card is outdated or invalid.");
        }
      }
    } catch (error) {
      console.error("Error reading daily card from localStorage:", error);
      // Clear potentially corrupted data
      localStorage.removeItem(storageKey);
    }

    if (storedCard) {
      setDailyCard(storedCard);
    } else {
      // No valid card stored for today, draw a new one
      const newCard = getRandomCard(tarotCards);
      // console.log("Drawing and storing new daily card for today:", newCard);
      setDailyCard(newCard);
      // Store the new card and today's date
      try {
        const dataToStore = { card: newCard, dateDrawn: todaysDate };
        localStorage.setItem(storageKey, JSON.stringify(dataToStore));
      } catch (error) {
        console.error("Error saving daily card to localStorage:", error);
      }
    }
  }, []);

  const handleZodiacChange = (event) => {
    setSelectedZodiac(event.target.value);
  };

  const handleRisingSignChange = (event) => {
    setSelectedRisingSign(event.target.value);
  };

  return (
    // Centered layout, max-width, adjusted padding, subtle background
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-start text-left p-6 md:p-12 font-sans">
      <div className="w-full max-w-3xl">
        {" "}
        {/* Content wrapper */}
        {/* Title - Slightly smaller, adjusted margin */}
        <h1 className="text-5xl md:text-6xl font-black leading-tight mb-1 md:mb-2">
          <span className="text-black">README</span>
          <span className="text-custom-red">tarot</span>
        </h1>
        {/* Subtitle - Adjusted margin */}
        <p className="text-lg md:text-xl text-gray-600 mb-10 italic">
          Draw your own conclusion.
        </p>
        {/* Unlock Deeper Insights Text - Adjusted margin */}
        <p className="text-base font-medium text-gray-700 mb-2">
          Select your zodiac sign to unlock deeper insights. Add your rising
          sign for an even more personalized and powerful reading.
        </p>
        {/* Link to find Rising Sign - Adjusted margin */}
        <p className="text-sm text-gray-600 mb-6">
          If you don't know your Rising Sign, you can find it here:{" "}
          <a
            href="https://www.astro.com/cgi/ade.cgi"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-800 hover:underline"
          >
            astro.com
          </a>
        </p>
        {/* Zodiac Selection Dropdowns - Always stacked */}
        <div className="flex flex-col gap-4 mb-10">
          {/* Sun Sign Dropdown */}
          <div>
            <label
              htmlFor="zodiac-select"
              className="block text-base font-medium text-gray-700 mb-1.5"
            >
              Select Sun Sign:
            </label>
            <select
              id="zodiac-select"
              name="zodiac"
              value={selectedZodiac}
              onChange={handleZodiacChange}
              // Slightly refined dropdown style
              className="block w-full max-w-xs px-4 pr-10 py-2 bg-white text-black text-sm uppercase rounded-none border border-gray-400 focus:outline-none focus:ring-1 focus:ring-black focus:border-black appearance-none"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, // Gray arrow
                backgroundPosition: "right 0.75rem center",
                backgroundRepeat: "no-repeat",
                backgroundSize: "1.5em 1.5em",
              }}
            >
              <option value="" className="text-gray-500 bg-white">
                -- Select
              </option>
              {ZODIAC_SIGNS.map((sign) => (
                <option key={sign} value={sign} className="text-black bg-white">
                  {sign}
                </option>
              ))}
            </select>
          </div>

          {/* Rising Sign Dropdown */}
          <div className="relative group">
            <label
              htmlFor="rising-sign-select"
              className="block text-base font-medium text-gray-700 mb-1.5 cursor-help"
            >
              Select Rising Sign:
            </label>
            {/* Custom Tooltip */}
            <div
              className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-96 
                         invisible group-hover:visible 
                         bg-[#b6b0a5] text-black text-base py-2 px-4 shadow-lg 
                         opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"
            >
              Your Rising Sign, also known as the Ascendant, is the zodiac sign
              that was ascending on the eastern horizon at the exact time and
              location of your birth. It represents the 'mask' you wear, the
              first impression you make on others, and your instinctive approach
              to new situations. It colors your outward personality, influences
              your physical appearance and mannerisms, and sets the stage for
              how you engage with the world. Discover the unique energy you
              project!
              {/* Add a small triangle/arrow - Keeping this */}
              <div className="absolute left-1/2 transform -translate-x-1/2 top-full w-0 h-0 border-x-8 border-x-transparent border-t-8 border-t-[#b6b0a5]"></div>
            </div>
            <select
              id="rising-sign-select"
              name="rising-sign"
              value={selectedRisingSign}
              onChange={handleRisingSignChange}
              // Slightly refined dropdown style
              className="block w-full max-w-xs px-4 pr-10 py-2 bg-white text-black text-sm uppercase rounded-none border border-gray-400 focus:outline-none focus:ring-1 focus:ring-black focus:border-black appearance-none"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, // Gray arrow
                backgroundPosition: "right 0.75rem center",
                backgroundRepeat: "no-repeat",
                backgroundSize: "1.5em 1.5em",
              }}
            >
              <option value="" className="text-gray-500 bg-white">
                -- Optional
              </option>
              {ZODIAC_SIGNS.map((sign) => (
                <option key={sign} value={sign} className="text-black bg-white">
                  {sign}
                </option>
              ))}
            </select>
          </div>
        </div>
        {/* Begin Reading Button - Adjusted width, margin, border */}
        <Link
          to="/tarot"
          className="block w-full max-w-xs px-6 py-2.5 bg-white text-black border border-black text-base font-medium uppercase tracking-wider rounded-none transition-colors duration-200 transform hover:bg-custom-red hover:text-white hover:border-custom-red focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-50 focus:ring-black mb-12 text-center"
        >
          BEGIN 3 CARD DRAW
        </Link>
        {/* Daily Reading Section - Ensure it aligns within the max-width container */}
        {dailyCard && (
          <div className="w-full flex justify-start mb-6">
            {" "}
            {/* Adjusted alignment if needed */}
            <DailyReading card={dailyCard} />
          </div>
        )}
      </div>{" "}
      {/* Close content wrapper */}
    </div>
  );
}
